import { createIcons, Bell, HelpCircle, UserCircle, LogOut, User, Menu, ShieldCheck, ChevronDown } from "lucide";
import { contextStore } from "../auth/context-store.js";
import { PermissionCode } from "../../permissions/permissions.js";
import { router } from "../router/router.js";
import { notificationService } from "../../application/services/notification-service.js";
import { userInitials } from "../../domain/identity/user-avatar.js";
// [COMANDO ÚNICO] Import removido: seletor global de Cliente não está mais no cabeçalho.

class AppTopbar extends HTMLElement {
  private unsubscribe: (() => void) | null = null;
  private isNotifOpen = false;
  private isUserOpen = false;
  private isProfileModalOpen = false;
  private editName = "";
  /** Profile photo of the signed-in user (data URL), or null for the initials avatar. */
  private currentUserPhoto: string | null = null;
  private unreadCount = 0;
  private notifications: any[] = [];
  private isLoadingNotifications = false;

  private async loadNotifications() {
    this.isLoadingNotifications = true;
    try {
      const ctx = contextStore.get();
      if (ctx && ctx.userId) {
        this.unreadCount = await notificationService.getUnreadCount(ctx.userId);
        this.notifications = await notificationService.getNotifications(ctx.userId);
      } else {
        this.unreadCount = 0;
        this.notifications = [];
      }
    } catch (e) {
      console.error('Failed to load notifications', e);
    } finally {
      this.isLoadingNotifications = false;
      this.render();
    }
  }
  
  /** Photo of the signed-in user, or their initials — never a broken image. */
  private renderUserAvatar(name: string, size: number): string {
    const common = `width:${size}px; height:${size}px; border-radius:50%; flex-shrink:0;`;
    if (this.currentUserPhoto) {
      return `<img src="${this.currentUserPhoto}" alt="" style="${common} object-fit: cover; border:1px solid rgba(255,255,255,0.25);">`;
    }
    return `<div style="${common} background: rgba(255,255,255,0.14); display:flex; align-items:center; justify-content:center; font-size:${Math.round(size * 0.38)}px; font-weight:700;">${userInitials(name)}</div>`;
  }

  /** Loads the photo of whoever is signed in, by userId. */
  private async loadCurrentUserPhoto() {
    const ctx = contextStore.get();
    if (!ctx.userId) {
      this.currentUserPhoto = null;
      return;
    }
    try {
      const p = (window as any).app?.platform;
      if (!p?.adminService) return;
      // Sem inflar o contexto: o próprio avatar é lido pelo caminho do dono,
      // que trabalha sobre `context.userId` e não exige `identity.read`.
      const photo = await p.adminService.getOwnProfileImage(ctx as any);
      if (photo !== this.currentUserPhoto) {
        this.currentUserPhoto = photo;
        this.render();
      }
    } catch {
      this.currentUserPhoto = null;
    }
  }

  connectedCallback() {
    this.loadCurrentUserPhoto();
    this.loadNotifications();
    this.unsubscribe = contextStore.subscribe(() => {
      this.loadNotifications();
      // Switching user must show that user's own photo, never the previous one.
      this.loadCurrentUserPhoto();
      this.render();
    });
    
    // Subscribe to notification updates
    window.addEventListener('notifications-updated', this.handleNotificationUpdate);
    
    // Close popovers on click outside
    document.addEventListener('click', this.handleClickOutside);
  }

  disconnectedCallback() {
    if (this.unsubscribe) this.unsubscribe();
    window.removeEventListener('notifications-updated', this.handleNotificationUpdate);
    document.removeEventListener('click', this.handleClickOutside);
  }

  private handleNotificationUpdate = () => {
    this.loadNotifications();
  };

  private handleClickOutside = (e: MouseEvent) => {
    const target = e.target as HTMLElement;
    if (!target.closest('.notif-container') && this.isNotifOpen) {
      this.isNotifOpen = false;
      this.render();
    }
    if (!target.closest('.user-container') && this.isUserOpen) {
      this.isUserOpen = false;
      this.render();
    }
  };

  render() {
    const ctx = contextStore.get();
    const unreadCount = this.unreadCount;
    const notifications = this.notifications;
    
    // Badge de perfil — escala grafite consistente com o design system
    const roleBadgeStyle = "background: rgba(255,255,255,0.1); color: var(--color-graphite-200); border: 1px solid rgba(255,255,255,0.2); padding: 2px 10px; border-radius: 2px; font-size: 0.6875rem; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; display: inline-flex; align-items: center; gap: 5px;";

    this.innerHTML = `
      <div class="topbar-left" style="display: flex; align-items: center; gap: 12px;">
        <button id="btn-toggle-sidebar" style="background: none; border: none; color: var(--color-white); cursor: pointer; display: flex; align-items: center; justify-content: center; padding: 4px;">
          <i data-lucide="menu" style="width: 24px; height: 24px;"></i>
        </button>
        <!-- [COMANDO ÚNICO] Seletor global de Cliente removido do cabeçalho.
             Telas que precisam de contexto de Cliente usam filtros locais próprios. -->
      </div>
      <div class="topbar-right" style="margin-left: auto; display: flex; align-items: center; gap: 14px;">
        <!-- RBAC Role Badge -->
        <div class="topbar-decor" style="${roleBadgeStyle}">
          <i data-lucide="shield-check" style="width: 12px; height: 12px;"></i>
          <span>${ctx.roleDisplay || ctx.role}</span>
        </div>

        <span class="topbar-decor" style="font-size: 0.6875rem; color: var(--color-graphite-400); letter-spacing: 0.04em; text-transform: uppercase;">
          ${contextStore.hasPermission(PermissionCode.AiReviewRead) ? "IA Review: motor local" : ""}
        </span>
        
        <!-- Notifications -->
        <div class="notif-container" style="position: relative; cursor: pointer;">
          <div id="btn-notif" style="position: relative; padding: 4px;">
            <i data-lucide="bell" style="width: 20px; height: 20px; color: var(--color-graphite-300);"></i>
            ${unreadCount > 0 ? `
              <div style="position: absolute; top: 0; right: 0; background: var(--status-danger-solid); color: var(--color-text-primary); font-size: 0.65rem; font-weight: 700; width: 16px; height: 16px; display: flex; align-items: center; justify-content: center; border-radius: 50%;">
                ${unreadCount}
              </div>
            ` : ''}
          </div>
          
          ${this.isNotifOpen ? `
            <div style="position: absolute; top: 44px; right: 0; width: 300px; background: var(--color-surface-primary); border-radius: var(--radius-md); box-shadow: var(--shadow-xl); border: 1px solid var(--color-border-default); z-index: 1060; overflow: hidden; display: flex; flex-direction: column;">
              <div style="padding: 10px 14px; border-bottom: 1px solid var(--color-border-default); display: flex; justify-content: space-between; align-items: center; background: var(--color-surface-secondary);">
                <span style="font-weight: 600; font-size: 0.8125rem; color: var(--color-text-primary);">Notificações</span>
                ${unreadCount > 0 ? `
                  <button id="btn-mark-read" style="background: none; border: none; font-size: 0.75rem; color: var(--color-text-muted); cursor: pointer; font-weight: 500; transition: color 0.15s;">Marcar lidas</button>
                ` : ''}
              </div>
              <div style="max-height: 280px; overflow-y: auto;">
                ${notifications.length === 0 ? `
                  <div style="padding: 24px; text-align: center; color: var(--color-text-muted); font-size: 0.8125rem;">Nenhuma notificação.</div>
                ` : notifications.map(n => `
                  <div class="notif-item" data-id="${n.id}" style="cursor: pointer; padding: 10px 14px; border-bottom: 1px solid var(--color-border-subtle); background: ${n.read ? 'var(--color-surface-primary)' : 'var(--color-bg-secondary)'}; opacity: ${n.read ? '0.75' : '1'}; transition: background 0.15s;">
                    <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 3px;">
                      <div style="font-weight: 600; font-size: 0.8125rem; color: var(--color-text-primary);">${n.title}</div>
                      ${!n.read ? `<div style="width: 7px; height: 7px; background: var(--color-graphite-600); border-radius: 50%; flex-shrink: 0; margin-top: 3px;"></div>` : ''}
                    </div>
                    <div style="font-size: 0.75rem; color: var(--color-text-secondary); line-height: 1.4;">${n.message}</div>
                    <div style="font-size: 0.625rem; color: var(--color-text-muted); margin-top: 4px; font-family: var(--font-mono);">${new Date(n.createdAt).toLocaleString('pt-BR')}</div>
                  </div>
                `).join('')}
              </div>
            </div>
          ` : ''}
        </div>
        
        <div class="topbar-decor" style="cursor: pointer;"><i data-lucide="help-circle" style="width: 20px; height: 20px; color: var(--color-graphite-300);"></i></div>
        
        <!-- User Menu & RBAC Switcher -->
        <div class="user-container" style="position: relative;">
          <div id="btn-user" style="display: flex; align-items: center; gap: 8px; cursor: pointer; padding: 6px 10px; border-radius: var(--radius-sm); transition: background var(--transition-fast); background: ${this.isUserOpen ? 'rgba(255,255,255,0.1)' : 'transparent'};">
            ${this.renderUserAvatar(ctx.userName, 26)}
            <div class="topbar-decor" style="display: flex; flex-direction: column; text-align: left; line-height: 1.1;">
              <span style="font-size: 0.825rem; font-weight: 600;">${ctx.userName}</span>
              <span style="font-size: 0.6875rem; color: var(--color-graphite-400);">${ctx.roleDisplay}</span>
            </div>
            <i data-lucide="chevron-down" class="topbar-decor" style="width: 14px; height: 14px; margin-left: 2px; opacity: 0.7;"></i>
          </div>
          
          ${this.isUserOpen ? `
            <div style="position: absolute; top: 46px; right: 0; width: 272px; background: var(--color-surface-primary); border-radius: var(--radius-md); box-shadow: var(--shadow-xl); border: 1px solid var(--color-border-default); z-index: 1060; overflow: hidden; padding: 4px 0;">
              <div style="padding: 12px 14px; border-bottom: 1px solid var(--color-border-subtle); background: var(--color-surface-secondary);">
                <div style="font-weight: 600; font-size: 0.875rem; color: var(--color-text-primary);">${ctx.userName}</div>
                <div style="font-size: 0.75rem; color: var(--color-text-secondary); margin-top: 1px;">${ctx.userEmail || ""}</div>
                <div style="margin-top: 6px; display: inline-flex; align-items: center; gap: 4px; font-size: 0.625rem; font-weight: 700; color: var(--color-text-secondary); background: var(--color-surface-secondary); padding: 2px 8px; border-radius: 2px; text-transform: uppercase; letter-spacing: 0.05em;">
                  <span>${ctx.roleDisplay}</span>
                </div>
              </div>

              <button id="btn-switch-user" style="width: 100%; display: flex; align-items: center; gap: 8px; padding: 8px 14px; background: none; border: none; text-align: left; font-size: 0.8125rem; color: var(--color-text-primary); cursor: pointer; transition: background var(--transition-fast);" onmouseover="this.style.background='var(--color-bg-secondary)'" onmouseout="this.style.background='none'">
                <i data-lucide="user-circle" style="width: 14px; height: 14px; color: var(--color-text-secondary);"></i> Trocar de Usuário
              </button>

              <button id="btn-profile" style="width: 100%; display: flex; align-items: center; gap: 8px; padding: 8px 14px; background: none; border: none; text-align: left; font-size: 0.8125rem; color: var(--color-text-primary); cursor: pointer; transition: background var(--transition-fast);" onmouseover="this.style.background='var(--color-bg-secondary)'" onmouseout="this.style.background='none'">
                <i data-lucide="user" style="width: 14px; height: 14px; color: var(--color-text-secondary);"></i> Meu Perfil
              </button>
              <button id="btn-logout" style="width: 100%; display: flex; align-items: center; gap: 8px; padding: 8px 14px; background: none; border: none; text-align: left; font-size: 0.8125rem; color: var(--status-danger-text); cursor: pointer; transition: background var(--transition-fast);" onmouseover="this.style.background='var(--status-danger-bg)'" onmouseout="this.style.background='none'">
                <i data-lucide="log-out" style="width: 14px; height: 14px;"></i> Sair do Sistema
              </button>
            </div>
          ` : ''}
        </div>
      </div>
      
      ${this.isProfileModalOpen ? this.renderProfileModal(ctx) : ''}
    `;

    createIcons({
      icons: { Bell, HelpCircle, UserCircle, LogOut, User, Menu, ShieldCheck, ChevronDown },
      nameAttr: "data-lucide",
      root: this
    });

    this.attachEvents();
  }

  private attachEvents() {
    this.querySelector("#btn-toggle-sidebar")?.addEventListener("click", () => {
      const shell = document.querySelector("app-shell");
      if (!shell) return;
      // Mobile (<768px): o mesmo ☰ abre/fecha o Drawer da Sidebar (item: Sidebar → Drawer).
      // Desktop/Tablet: continua recolhendo a Sidebar para ícones, como já era.
      if (window.innerWidth < 768) {
        shell.toggleAttribute("mobile-nav-open");
      } else if (shell.hasAttribute("collapsed")) {
        shell.removeAttribute("collapsed");
      } else {
        shell.setAttribute("collapsed", "");
      }
    });

    this.querySelector("#btn-notif")?.addEventListener("click", (e) => {
      e.stopPropagation();
      this.isNotifOpen = !this.isNotifOpen;
      this.isUserOpen = false;
      this.render();
    });

    this.querySelector("#btn-user")?.addEventListener("click", (e) => {
      e.stopPropagation();
      this.isUserOpen = !this.isUserOpen;
      this.isNotifOpen = false;
      this.render();
    });

    // Trocar de usuário é uma NOVA AUTENTICAÇÃO: encerra a sessão e volta à
    // tela de acesso. Não existe lista de contas para assumir sem senha.
    this.querySelector("#btn-switch-user")?.addEventListener("click", (e) => {
      e.stopPropagation();
      this.isUserOpen = false;
      contextStore.requireReauthentication();
    });

    this.querySelector("#btn-mark-read")?.addEventListener("click", async (e) => {
      e.stopPropagation();
      const ctx = contextStore.get();
      if (ctx.userId) {
        await notificationService.markAllAsRead(ctx.userId);
      }
    });

    this.querySelector("#btn-logout")?.addEventListener("click", () => {
      contextStore.logout();
      this.isUserOpen = false;
      router.navigate("/login");
    });

    this.querySelector("#btn-profile")?.addEventListener("click", (e) => {
      e.stopPropagation();
      this.isUserOpen = false;
      this.isProfileModalOpen = true;
      this.editName = contextStore.get().userName;
      this.render();
    });

    this.querySelectorAll(".notif-item").forEach(item => {
      item.addEventListener("click", async (e) => {
        e.stopPropagation();
        const id = (e.currentTarget as HTMLElement).getAttribute("data-id");
        if (id) {
          await notificationService.markAsRead(id);
          const notif = this.notifications.find(n => n.id === id);
          if (notif) {
             this.isNotifOpen = false;
             if (notif.route) {
               // Notificação com destino (correção por item, HOLD, revisão): abre o documento na seção/item exatos.
               router.navigate(notif.route);
             } else if (notif.entityType === 'Report' && notif.reportId) {
               router.navigate(`/reports/${notif.reportId}`);
             } else if (notif.entityType === 'Inspection' && notif.inspectionId) {
               router.navigate(`/inspections/${notif.inspectionId}`);
             } else if (notif.entityType === 'SubWorkOrder') {
               router.navigate(`/management/os`);
             } else if (notif.entityType === 'AVR' && notif.entityId) {
               // PLH vai para a análise; o Engenheiro volta à elaboração.
               router.navigate(notif.event === 'AVR_SUBMITTED_FOR_PLH_REVIEW' ? `/engineering/avr/${notif.entityId}` : `/integrity/avr/${notif.entityId}`);
             }
             this.render();
          }
        }
      });
    });

    this.querySelector("#profile-name-input")?.addEventListener("input", (e) => {
      this.editName = (e.target as HTMLInputElement).value;
    });

    this.querySelector("#btn-save-profile")?.addEventListener("click", (e) => {
      e.stopPropagation();
      contextStore.set({ userName: this.editName });
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('inspect_user_name', this.editName);
      }
      this.isProfileModalOpen = false;
      this.render();
    });

    this.querySelector("#btn-close-profile")?.addEventListener("click", (e) => {
      e.stopPropagation();
      this.isProfileModalOpen = false;
      this.render();
    });
  }

  private renderProfileModal(ctx: any): string {
    return `
      <div class="modal-overlay">
        <div class="modal-content" style="max-width: 440px;" role="dialog" aria-modal="true" aria-labelledby="profile-modal-title">
          <div class="modal-header">
            <h3 id="profile-modal-title" class="text-subsection-title">Meu Perfil</h3>
            <button id="btn-close-profile" class="btn-icon" aria-label="Fechar">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
            </button>
          </div>
          <div class="modal-body" style="display: flex; flex-direction: column; gap: 14px;">
            <div class="form-group">
              <label class="form-label">Nome de Exibição</label>
              <input id="profile-name-input" type="text" class="form-input" value="${this.editName}">
            </div>
            <div class="field-grid-2">
              <div class="form-group">
                <label class="form-label">Perfil Ativo</label>
                <input type="text" class="form-input" value="${ctx.roleDisplay}" disabled>
              </div>
              <div class="form-group">
                <label class="form-label">Profissional</label>
                <input type="text" class="form-input" value="${ctx.professionalId || 'Não vinculado'}" disabled>
              </div>
            </div>
            <div class="form-group">
              <label class="form-label">Permissões Concedidas</label>
              <div style="max-height: 100px; overflow-y: auto; background: var(--color-surface-secondary); border: 1px solid var(--color-border-subtle); border-radius: var(--radius-sm); padding: 8px 10px; font-size: 0.6875rem; color: var(--color-text-secondary); font-family: var(--font-mono); line-height: 1.6;">
                ${Array.from(ctx.permissions || []).join(" • ") || "Nenhuma permissão"}
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button id="btn-save-profile" class="btn btn-primary btn-sm">Salvar Alterações</button>
          </div>
        </div>
      </div>
    `;
  }
}

if (!customElements.get("app-topbar")) {
  customElements.define("app-topbar", AppTopbar);
}



